import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, MapPin, Users, MessageCircle, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, WHATSAPP_LINK } from "../lib/api";

const initial = {
  full_name: "", tc_no: "", birth_date: "", age: "", gender: "",
  phone: "", email: "", parent_name: "", parent_phone: "",
  emergency_contact: "", emergency_phone: "",
  branch_or_event: "", health_declaration: "", sports_history: "",
  license_status: "", club_info: "", body_size: "", address: "",
  kvkk_consent: false, explicit_consent: false, rules_consent: false,
};

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(initial);

  useEffect(() => {
    setLoading(true);
    api.get(`/events/${id}`)
      .then((r) => {
        setEvent(r.data);
        setForm((f) => ({ ...f, branch_or_event: r.data.name }));
      })
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, [id]);

  const update = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.kvkk_consent || !form.explicit_consent || !form.rules_consent) {
      toast.error("Lütfen tüm onayları kabul edin.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/event-registrations", {
        ...form,
        event_id: event.id,
        age: parseInt(form.age, 10) || 0,
      });
      setSuccess(true);
      toast.success("Kaydınız alınmıştır!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Kayıt sırasında bir hata oluştu.";
      toast.error(typeof msg === "string" ? msg : "Form gönderilemedi.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="container-x py-32 text-center text-muted-foreground">Yükleniyor...</div>;
  }
  if (!event) {
    return (
      <div className="container-x py-32 text-center">
        <h1 className="heading-lg">Etkinlik bulunamadı</h1>
        <Link to="/etkinlikler" className="btn-primary mt-6"><ArrowLeft className="h-4 w-4" />Etkinliklere Dön</Link>
      </div>
    );
  }

  return (
    <div data-testid={`event-detail-${id}`}>
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute -top-40 -right-40 h-[480px] w-[480px] rounded-full bg-primary/15 blur-3xl" />
        <div className="container-x relative py-14 md:py-20">
          <Link to="/etkinlikler" className="btn-ghost mb-5 -ml-2"><ArrowLeft className="h-4 w-4" />Tüm Etkinlikler</Link>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="badge-status-soon">{event.event_type}</span>
            <span className="text-xs font-display uppercase tracking-[0.22em] text-muted-foreground">{event.branch}</span>
          </div>
          <h1 className="heading-xl">{event.name}</h1>
          <p className="mt-5 max-w-3xl text-base md:text-lg text-muted-foreground leading-relaxed">{event.description}</p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x grid lg:grid-cols-12 gap-8">
          {/* INFO SIDE */}
          <aside className="lg:col-span-4 lg:order-2 space-y-4 lg:sticky lg:top-28 self-start">
            <div className="card-elev p-6">
              <h3 className="font-display text-lg font-bold uppercase tracking-wider mb-4">Etkinlik Bilgileri</h3>
              <ul className="space-y-3 text-sm">
                <Row icon={Calendar} label="Tarih" value={`${event.date} · ${event.time}`} />
                <Row icon={MapPin} label="Konum" value={event.location} />
                <Row icon={Users} label="Yaş Kategorisi" value={event.age_category} />
                <Row icon={Users} label="Kontenjan" value={String(event.capacity)} />
                <Row icon={Clock} label="Son Başvuru" value={event.deadline} />
              </ul>
              <div className="mt-5 pt-5 border-t border-border">
                <div className="text-[10px] font-display uppercase tracking-[0.22em] text-muted-foreground">Katılım Şartları</div>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{event.requirements}</p>
              </div>
              <div className="mt-5 pt-5 border-t border-border">
                <div className="text-[10px] font-display uppercase tracking-[0.22em] text-muted-foreground">Sorumlu Kişi</div>
                <p className="mt-1 text-sm">{event.contact_person}</p>
              </div>
              <a
                href={WHATSAPP_LINK}
                target="_blank" rel="noreferrer"
                className="btn-primary w-full mt-5"
                data-testid="event-whatsapp"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp ile Sor
              </a>
            </div>
          </aside>

          {/* FORM SIDE */}
          <div className="lg:col-span-8 lg:order-1">
            {success ? (
              <div className="card-elev p-10 text-center" data-testid="registration-success">
                <div className="h-16 w-16 mx-auto rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/30 flex items-center justify-center text-emerald-400 mb-5">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <h2 className="heading-md">Kaydınız Alınmıştır</h2>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto leading-relaxed">
                  Focus Spor Kulübü ekibi en kısa sürede sizinle iletişime geçecektir.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="btn-primary">
                    <MessageCircle className="h-4 w-4" /> WhatsApp'a Yönlendir
                  </a>
                  <Link to="/etkinlikler" className="btn-secondary">Diğer Etkinlikler</Link>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="card-elev p-6 md:p-8" data-testid="registration-form">
                <div className="eyebrow mb-3">Katılımcı Kayıt</div>
                <h2 className="heading-md mb-6">Etkinliğe Kayıt Formu</h2>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Ad Soyad" required>
                    <input className="input-base" required value={form.full_name} onChange={update("full_name")} data-testid="reg-full-name" />
                  </Field>
                  <Field label="T.C. Kimlik No" required>
                    <input className="input-base" required value={form.tc_no} onChange={update("tc_no")} maxLength={11} data-testid="reg-tc" />
                  </Field>
                  <Field label="Doğum Tarihi" required>
                    <input type="date" className="input-base" required value={form.birth_date} onChange={update("birth_date")} data-testid="reg-birth" />
                  </Field>
                  <Field label="Yaş" required>
                    <input type="number" min="3" max="99" className="input-base" required value={form.age} onChange={update("age")} data-testid="reg-age" />
                  </Field>
                  <Field label="Cinsiyet" required>
                    <select className="input-base" required value={form.gender} onChange={update("gender")} data-testid="reg-gender">
                      <option value="">Seçiniz</option>
                      <option value="Kadın">Kadın</option>
                      <option value="Erkek">Erkek</option>
                      <option value="Belirtmek istemiyorum">Belirtmek istemiyorum</option>
                    </select>
                  </Field>
                  <Field label="Telefon" required>
                    <input className="input-base" required value={form.phone} onChange={update("phone")} placeholder="05XX XXX XX XX" data-testid="reg-phone" />
                  </Field>
                  <Field label="E-posta" required>
                    <input type="email" className="input-base" required value={form.email} onChange={update("email")} data-testid="reg-email" />
                  </Field>
                  <Field label="Beden / Forma Ölçüsü">
                    <input className="input-base" value={form.body_size} onChange={update("body_size")} placeholder="S / M / L / XL" data-testid="reg-size" />
                  </Field>
                  <Field label="Veli Adı Soyadı (18 yaş altı)">
                    <input className="input-base" value={form.parent_name} onChange={update("parent_name")} data-testid="reg-parent" />
                  </Field>
                  <Field label="Veli Telefon">
                    <input className="input-base" value={form.parent_phone} onChange={update("parent_phone")} data-testid="reg-parent-phone" />
                  </Field>
                  <Field label="Acil Durumda Aranacak Kişi" required>
                    <input className="input-base" required value={form.emergency_contact} onChange={update("emergency_contact")} data-testid="reg-emergency" />
                  </Field>
                  <Field label="Acil Durum Telefonu" required>
                    <input className="input-base" required value={form.emergency_phone} onChange={update("emergency_phone")} data-testid="reg-emergency-phone" />
                  </Field>
                  <Field label="Lisans Durumu">
                    <select className="input-base" value={form.license_status} onChange={update("license_status")} data-testid="reg-license">
                      <option value="">Seçiniz</option>
                      <option value="Lisanslı">Lisanslı</option>
                      <option value="Lisanssız">Lisanssız</option>
                    </select>
                  </Field>
                  <Field label="Bağlı Olduğu Kulüp">
                    <input className="input-base" value={form.club_info} onChange={update("club_info")} placeholder="Yoksa boş bırakın" data-testid="reg-club" />
                  </Field>
                </div>

                <div className="grid gap-4 mt-4">
                  <Field label="Adres" required>
                    <textarea className="input-base min-h-[80px]" required value={form.address} onChange={update("address")} data-testid="reg-address" />
                  </Field>
                  <Field label="Sağlık Durumu Beyanı" required>
                    <textarea className="input-base min-h-[80px]" required placeholder="Bilinen rahatsızlık, alerji vb. yoksa 'yok' yazınız." value={form.health_declaration} onChange={update("health_declaration")} data-testid="reg-health" />
                  </Field>
                  <Field label="Spor Geçmişi">
                    <textarea className="input-base min-h-[60px]" placeholder="Daha önce ilgilendiğiniz branşlar" value={form.sports_history} onChange={update("sports_history")} data-testid="reg-history" />
                  </Field>
                </div>

                <div className="mt-6 space-y-3">
                  <Consent checked={form.kvkk_consent} onChange={update("kvkk_consent")} testid="reg-kvkk">
                    <span className="font-semibold text-foreground">KVKK Aydınlatma Metni</span>'ni okudum, kabul ediyorum.
                  </Consent>
                  <Consent checked={form.explicit_consent} onChange={update("explicit_consent")} testid="reg-explicit">
                    Kişisel verilerimin kayıt amacıyla işlenmesine <span className="font-semibold text-foreground">açık rıza</span> veriyorum.
                  </Consent>
                  <Consent checked={form.rules_consent} onChange={update("rules_consent")} testid="reg-rules">
                    Etkinlik kurallarını okudum ve <span className="font-semibold text-foreground">kabul ediyorum</span>.
                  </Consent>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full mt-7 disabled:opacity-60"
                  data-testid="reg-submit"
                >
                  {submitting ? "Gönderiliyor..." : "Başvuruyu Gönder"} {!submitting && <ArrowRight className="h-4 w-4" />}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label-input">{label}{required && <span className="text-primary"> *</span>}</span>
      {children}
    </label>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <li className="flex items-start gap-3">
      <Icon className="h-4 w-4 mt-0.5 text-primary shrink-0" />
      <div>
        <div className="text-[10px] font-display uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        <div>{value}</div>
      </div>
    </li>
  );
}

function Consent({ checked, onChange, testid, children }) {
  return (
    <label className="flex items-start gap-3 text-sm cursor-pointer">
      <input type="checkbox" className="mt-1 h-4 w-4 accent-[hsl(202_100%_50%)]" checked={checked} onChange={onChange} data-testid={testid} />
      <span className="text-muted-foreground leading-relaxed">{children}</span>
    </label>
  );
}
